import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Server,
    HardDrive,
    Monitor,
    Cloud,
    CloudUpload,
    CloudDownload,
    CheckCircle2,
    Circle,
    GitBranch,
    RotateCcw,
    FileBarChart,
    Terminal,
    AppWindow,
    Network,
    ShieldCheck,
    Database,
    Lock,
    Boxes,
    Layers,
    Wrench,
} from "lucide-react";
import DemoRequestModal from "@/components/DemoRequestModal";
import {
    DOWNLOAD_HREF,
    TopBar,
    FooterBar,
    SectionHeader,
    WindowsChrome,
    AppSidebar,
} from "@/components/marketing/Chrome";

/**
 * Capabilities — dedicated deep-dive page for the 6 migration types.
 * Rebuilt to match a detailed reference mockup the user provided (nav
 * active-state, hero migration-progress mockup, core-capability cards,
 * detailed-capability cards with per-capability mini visuals, an
 * enterprise-scale strip, and the 3-screenshot MoovBytes Desktop section).
 * Reuses the landing page's shared chrome (components/marketing/Chrome.jsx)
 * for nav/footer/window-mockup styling.
 */
export default function Capabilities() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const openContact = () => setContactOpen(true);

    return (
        <div className="relative min-h-screen overflow-x-hidden bg-[#050e22] text-white" data-testid="capabilities-page">
            <div className="pointer-events-none fixed inset-0 grid-bg opacity-[0.1]" />

            <TopBar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} openContact={openContact} />
            <Hero />
            <PathStrip />
            <CoreCapabilities />
            <DetailedCapabilities />
            <EnterpriseScale />
            <DesktopScreens />
            <FinalCTA openContact={openContact} />
            <FooterBar openContact={openContact} />
            <DemoRequestModal open={contactOpen} onClose={() => setContactOpen(false)} />
        </div>
    );
}

/* ────────────────────────────── Hero ────────────────────────────── */
function Hero() {
    return (
        <section className="relative z-10 mx-auto max-w-[1600px] px-6 pb-16 pt-14 sm:px-6 sm:pt-16" data-testid="capabilities-hero">
            <div className="pointer-events-none absolute -right-32 top-0 h-[460px] w-[640px] rounded-full bg-[#0066ff]/25 blur-[130px]" />

            <div className="relative grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:items-center">
                <div>
                    <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#3fb8ff]">Capabilities</div>
                    <h1 className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl">
                        Built to move data across PCs, servers, NAS systems, and{" "}
                        <span className="text-[#3fb8ff]">cloud environments.</span>
                    </h1>
                    <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-white/65 sm:text-[16px]">
                        MoovBytes is a Windows data migration application designed for
                        secure, resumable, and verified migrations. Move entire
                        systems, user data, applications, and more — with confidence.
                    </p>
                    <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row">
                        <Link
                            to={DOWNLOAD_HREF}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#0066ff] px-6 py-3.5 text-[14px] font-semibold text-white shadow-lg shadow-[#004AAD]/30 transition hover:brightness-110 sm:w-auto"
                        >
                            <AppWindow size={16} />
                            Download for Windows
                        </Link>
                        <Link
                            to="/documentation"
                            className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/[0.03] px-6 py-3.5 text-[14px] font-semibold text-white/85 transition hover:border-white/30 hover:bg-white/[0.06] hover:text-white sm:w-auto"
                        >
                            View Documentation
                        </Link>
                    </div>
                    <div className="mt-6 text-[12.5px] text-white/40">
                        Windows 10/11 &nbsp;•&nbsp; Windows Server 2019–2025 &nbsp;•&nbsp; 64-bit
                    </div>
                </div>

                <MigrationProgressVisual />
            </div>
        </section>
    );
}

function MigrationProgressVisual() {
    const LOG = [
        ["10:24:17", "Copying user data (C:\\Users)"],
        ["10:23:41", "Transferring application data"],
        ["10:20:18", "Processing filesystem metadata"],
        ["10:18:03", "Connection established"],
    ];
    return (
        <div className="relative" data-testid="capabilities-hero-visual">
            <div className="pointer-events-none absolute -inset-16 -z-10 rounded-full bg-[#0066ff]/30 blur-[110px]" />
            <WindowsChrome title="MoovBytes">
                <div className="flex gap-3">
                    <AppSidebar />
                    <div className="min-w-0 flex-1 space-y-3">
                        <div>
                            <div className="text-[13px] font-bold text-white">Migration in Progress</div>
                            <div className="text-[9px] text-white/40">Transferring data from WIN-DEV-01 to FS-01</div>
                        </div>

                        <div>
                            <div className="flex items-center gap-2">
                                <div className="h-1.5 flex-1 rounded-full bg-white/5">
                                    <div className="h-full rounded-full bg-gradient-to-r from-[#5b5ef0] to-[#00C2FF]" style={{ width: "78%" }} />
                                </div>
                                <span className="text-[10px] font-bold tabular-nums text-white/85">78%</span>
                            </div>
                            <div className="mt-1.5 flex items-center justify-between text-[8px] text-white/45">
                                <span>1.46 TB of 1.87 TB</span>
                                <span>2h 24m remaining</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-4 gap-2">
                            {[
                                ["Files Transferred", "1,582,941"],
                                ["Transfer Speed", "842 MB/s"],
                                ["Elapsed Time", "1h 12m"],
                                ["Status", "Transferring"],
                            ].map(([k, v]) => (
                                <div key={k} className="rounded-md border border-white/10 bg-white/[0.025] p-2">
                                    <div className="text-[7px] uppercase tracking-wide text-white/40">{k}</div>
                                    <div className={`mt-0.5 text-[10px] font-semibold ${k === "Status" ? "text-emerald-400" : "text-white/85"}`}>{v}</div>
                                </div>
                            ))}
                        </div>

                        <div className="rounded-md border border-white/10 bg-white/[0.025] p-2.5">
                            {LOG.map(([time, msg]) => (
                                <div key={time} className="flex items-center gap-2 py-0.5 text-[8px]">
                                    <span className="font-mono text-white/35">{time}</span>
                                    <CheckCircle2 size={9} className="shrink-0 text-emerald-400" />
                                    <span className="text-white/60">{msg}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </WindowsChrome>
            <div className="pointer-events-none absolute -left-[3px] top-9 h-10 w-[5px] rounded-full bg-[#00C2FF] shadow-[0_0_14px_3px_rgba(0,194,255,0.85)]" />
        </div>
    );
}

/* ─────────────────────────── Path strip ─────────────────────────── */
function PathStrip() {
    const PATHS = [
        { label: "PC to PC", icon: Monitor },
        { label: "Server to Server", icon: Server },
        { label: "NAS to NAS", icon: HardDrive },
        { label: "On-Prem to Cloud", icon: CloudUpload },
        { label: "Cloud to On-Prem", icon: CloudDownload },
        { label: "Cross-Environment", icon: Network },
    ];
    return (
        <section className="relative z-10 border-y border-white/5 bg-white/[0.015] px-6 py-8 sm:px-6" data-testid="capabilities-path-strip">
            <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-center gap-x-10 gap-y-5">
                {PATHS.map((p) => (
                    <div key={p.label} className="flex items-center gap-2.5 text-[13px] font-medium text-white/70">
                        <p.icon size={20} className="text-[#3fb8ff]" />
                        <span>{p.label}</span>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* ─────────────────────────── Core capabilities ─────────────────────────── */
const CORE = [
    { icon: Monitor, title: "PC to PC Migration", body: "Move files, folders, users, applications, and settings between Windows PCs with ease." },
    { icon: Server, title: "Server to Server Migration", body: "Migrate data between servers while preserving structure, permissions, and metadata." },
    { icon: HardDrive, title: "NAS / Network Share Migration", body: "Transfer data between SMB shares, NAS devices, and network storage systems." },
    { icon: Cloud, title: "Cloud Migration", body: "Move data to and from cloud platforms including Azure, AWS, and other S3-compatible storage." },
    { icon: ShieldCheck, title: "Data Verification", body: "Built-in SHA-256 verification ensures your data is transferred completely and correctly." },
    { icon: RotateCcw, title: "Resume & Recovery", body: "Pause and resume migrations at any time. Recover from interruptions without losing progress." },
];

function CoreCapabilities() {
    return (
        <section className="relative z-10 px-6 py-24 sm:px-6" data-testid="capabilities-core">
            <div className="mx-auto max-w-[1600px]">
                <SectionHeader
                    eyebrow="Core Capabilities"
                    title="Everything you need for Windows data migration."
                    body="Powerful capabilities for real-world environments, from single PCs to complex enterprise infrastructure."
                    align="left"
                />
                <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {CORE.map((c) => (
                        <div key={c.title} className="group flex items-start gap-4 rounded-xl border border-white/10 bg-white/[0.025] p-5 transition hover:border-[#00C2FF]/30 hover:bg-white/[0.04]">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#00C2FF]/30 bg-[#00C2FF]/10 text-[#3fb8ff]">
                                <c.icon size={18} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-[14px] font-bold text-white">{c.title}</div>
                                <p className="mt-1 text-[12.5px] leading-relaxed text-white/60">{c.body}</p>
                            </div>
                            <ArrowRight size={15} className="mt-1 shrink-0 text-white/25 transition group-hover:translate-x-0.5 group-hover:text-[#3fb8ff]" />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────────── Detailed capabilities ─────────────────────────── */
function DetailedCapabilities() {
    return (
        <section className="relative z-10 border-t border-white/5 bg-white/[0.015] px-6 py-24 sm:px-6" data-testid="capabilities-detailed">
            <div className="mx-auto max-w-[1600px]">
                <SectionHeader
                    eyebrow="Detailed Capabilities"
                    title="Designed for real-world migration scenarios."
                    body="From simple PC migrations to complex hybrid environments, MoovBytes provides the tools you need."
                    align="left"
                />
                <div className="mt-12 grid gap-5 lg:grid-cols-3">
                    <DetailCard title="Migration Paths" body="Flexible migration paths for any environment — PC to PC, server to server, NAS to NAS, on-prem to cloud, and more.">
                        <MigrationPathsVisual />
                    </DetailCard>
                    <DetailCard title="Verified Transfers" body="Every migration includes optional SHA-256 verification to ensure data integrity and complete transfers.">
                        <VerifiedTransfersVisual />
                    </DetailCard>
                    <DetailCard title="Resumable Migrations" body="Built to handle real-world conditions. Pause, resume, and recover migrations without starting over.">
                        <ResumableVisual />
                    </DetailCard>
                    <DetailCard title="Cross-Environment Support" body="Move data seamlessly between on-premises systems and cloud environments, with full support for hybrid workflows.">
                        <CrossEnvironmentVisual />
                    </DetailCard>
                    <DetailCard title="Enterprise Deployment" body="Deploy MoovBytes across your organization using standard Windows deployment tools.">
                        <DeploymentVisual />
                    </DetailCard>
                    <DetailCard title="Reporting & Visibility" body="Get detailed migration reports, track progress in real-time, and maintain visibility across all your migrations.">
                        <ReportingVisual />
                    </DetailCard>
                </div>
            </div>
        </section>
    );
}

function DetailCard({ title, body, children }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <div className="text-[15px] font-bold text-white">{title}</div>
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-white/60">{body}</p>
            <div className="mt-5">{children}</div>
        </div>
    );
}

function MiniBox({ label, icon: Icon }) {
    return (
        <div className="flex items-center gap-1.5 rounded border border-white/10 bg-white/[0.03] px-2 py-1.5 text-[9px] text-white/70">
            <Icon size={11} className="shrink-0 text-white/45" />
            <span className="truncate">{label}</span>
        </div>
    );
}

function MigrationPathsVisual() {
    const ENDPOINTS = [
        ["PCs", Monitor],
        ["Servers", Server],
        ["NAS / Shares", HardDrive],
        ["Cloud Storage", Cloud],
    ];
    return (
        <div className="rounded-lg border border-white/10 bg-[#050e22] p-3">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                <div className="space-y-1.5">
                    <div className="mb-1 text-[7px] uppercase tracking-wide text-white/35">Source</div>
                    {ENDPOINTS.map(([l, Icon]) => <MiniBox key={l} label={l} icon={Icon} />)}
                </div>
                <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#00C2FF]/40 bg-[#00C2FF]/10">
                    <img src="/brand/moovbytes-icon.png" alt="" className="h-5 w-5" />
                </div>
                <div className="space-y-1.5">
                    <div className="mb-1 text-[7px] uppercase tracking-wide text-white/35">Destination</div>
                    {ENDPOINTS.map(([l, Icon]) => <MiniBox key={l} label={l} icon={Icon} />)}
                </div>
            </div>
        </div>
    );
}

function VerifiedTransfersVisual() {
    return (
        <div className="rounded-lg border border-white/10 bg-[#050e22] p-3">
            <div className="mb-2 text-[8px] uppercase tracking-wide text-white/40">Verification Summary</div>
            <div className="flex items-center gap-1.5 text-[10px] font-semibold text-emerald-400">
                <CheckCircle2 size={13} /> Verification Completed
            </div>
            <div className="mt-1 text-[8px] text-white/45">All files verified successfully.</div>
            <div className="mt-2.5 space-y-1 border-t border-white/5 pt-2">
                {[["Files Verified", "1,582,941"], ["Data Verified", "1.87 TB"], ["Verification Method", "SHA-256"]].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-[9px]">
                        <span className="text-white/45">{k}</span>
                        <span className="text-white/80">{v}</span>
                    </div>
                ))}
                <div className="flex items-center justify-between text-[9px]">
                    <span className="text-white/45">Status</span>
                    <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 font-semibold text-emerald-400">Success</span>
                </div>
            </div>
        </div>
    );
}

function ResumableVisual() {
    const STEPS = [
        ["Scan source", "Complete", "done"],
        ["Transfer data", "In progress", "active"],
        ["Verify files", "Pending", "pending"],
        ["Finalize", "Pending", "pending"],
    ];
    return (
        <div className="rounded-lg border border-white/10 bg-[#050e22] p-3">
            <div className="mb-2 text-[8px] uppercase tracking-wide text-white/40">Migration Progress</div>
            <div className="space-y-1.5">
                {STEPS.map(([label, status, state]) => (
                    <div key={label} className="flex items-center justify-between text-[9.5px]">
                        <span className="flex items-center gap-1.5 text-white/75">
                            {state === "done" ? (
                                <CheckCircle2 size={12} className="text-emerald-400" />
                            ) : state === "active" ? (
                                <Circle size={12} className="fill-[#00C2FF] text-[#00C2FF]" />
                            ) : (
                                <Circle size={12} className="text-white/25" />
                            )}
                            {label}
                        </span>
                        <span className={state === "done" ? "text-emerald-400" : state === "active" ? "text-[#3fb8ff]" : "text-white/35"}>{status}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CrossEnvironmentVisual() {
    return (
        <div className="rounded-lg border border-white/10 bg-[#050e22] p-3">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                <div className="space-y-1.5">
                    <div className="mb-1 text-[7px] uppercase tracking-wide text-white/35">On-Premises</div>
                    <MiniBox label="Windows PCs" icon={Monitor} />
                    <MiniBox label="Windows Servers" icon={Server} />
                    <MiniBox label="NAS / Network Shares" icon={HardDrive} />
                </div>
                <Cloud size={20} className="text-[#3fb8ff]" />
                <div className="space-y-1.5">
                    <div className="mb-1 text-[7px] uppercase tracking-wide text-white/35">Cloud</div>
                    <MiniBox label="Microsoft Azure" icon={Cloud} />
                    <MiniBox label="Amazon S3" icon={Cloud} />
                    <MiniBox label="Other S3 Storage" icon={Cloud} />
                </div>
            </div>
        </div>
    );
}

function DeploymentVisual() {
    const DEPLOY = [
        ["EXE Installer", AppWindow],
        ["MSI Installer", Boxes],
        ["Intune", Cloud],
        ["Group Policy", Layers],
        ["RMM Tools", Wrench],
        ["PowerShell Deployment", Terminal],
    ];
    return (
        <div className="rounded-lg border border-white/10 bg-[#050e22] p-3">
            <div className="grid grid-cols-3 gap-2.5">
                {DEPLOY.map(([label, Icon]) => (
                    <div key={label} className="flex flex-col items-center gap-1.5 text-center">
                        <Icon size={16} className="text-[#3fb8ff]" />
                        <span className="text-[8px] leading-tight text-white/65">{label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ReportingVisual() {
    return (
        <div className="rounded-lg border border-white/10 bg-[#050e22] p-3">
            <div className="mb-2 text-[8px] uppercase tracking-wide text-white/40">Migration Report</div>
            <div className="space-y-1">
                {[["Total Files", "1,582,941"], ["Total Data", "1.87 TB"], ["Start Time", "Jan 15, 2025 10:18"], ["End Time", "Jan 15, 2025 12:42"]].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-[9px]">
                        <span className="text-white/45">{k}</span>
                        <span className="text-white/80">{v}</span>
                    </div>
                ))}
                <div className="flex items-center justify-between text-[9px]">
                    <span className="text-white/45">Status</span>
                    <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 font-semibold text-emerald-400">Completed</span>
                </div>
            </div>
            <button type="button" className="mt-2.5 w-full rounded border border-white/10 py-1.5 text-[9px] font-medium text-white/70">
                View Full Report
            </button>
        </div>
    );
}

/* ─────────────────────────── Enterprise scale ─────────────────────────── */
function EnterpriseScale() {
    const ITEMS = [
        { icon: Database, title: "Large Dataset Handling", body: "Optimized for multi-TB migrations with millions of files." },
        { icon: ShieldCheck, title: "SHA-256 Verification", body: "Ensure complete and accurate transfers." },
        { icon: GitBranch, title: "Block-Level Delta", body: "Transfer only changed data to reduce time and bandwidth." },
        { icon: Lock, title: "Secure Transfers", body: "Encrypted connections and secure authentication options." },
        { icon: FileBarChart, title: "Transfer Reporting", body: "Detailed logs and reports for compliance and audit needs." },
        { icon: Monitor, title: "Windows-First Workflow", body: "Built specifically for Windows environments and IT teams." },
    ];
    return (
        <section className="relative z-10 border-t border-white/5 px-6 py-24 sm:px-6" data-testid="capabilities-enterprise-scale">
            <div className="mx-auto max-w-[1600px]">
                <SectionHeader
                    eyebrow="Built for serious data moves"
                    title="Proven for enterprise-scale migrations."
                    body="MoovBytes is engineered to handle demanding migration projects with speed, reliability, and accuracy."
                    align="left"
                />
                <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 lg:grid-cols-6 lg:divide-x lg:divide-white/10">
                    {ITEMS.map((it) => (
                        <div key={it.title} className="lg:pl-6 lg:first:pl-0">
                            <it.icon size={22} className="text-[#3fb8ff]" />
                            <div className="mt-3 text-[12.5px] font-bold text-white">{it.title}</div>
                            <p className="mt-1 text-[11px] leading-relaxed text-white/55">{it.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────────── MoovBytes Desktop screenshots ─────────────────────────── */
function DesktopScreens() {
    const SCREENS = [
        { title: "Dashboard", body: "Get an overview of your migrations, recent activity, and system status.", mockup: <MockupDashboard />, activeNav: "Dashboard" },
        { title: "New Migration", body: "A guided workflow to select your source, destination, and options.", mockup: <MockupNewMigration />, activeNav: "New Migration" },
        { title: "Migration Details", body: "Track progress, view files, handle errors, and verify completed migrations.", mockup: <MockupMigrationDetails />, activeNav: "Migrations" },
    ];
    return (
        <section className="relative z-10 border-t border-white/5 bg-white/[0.015] px-6 py-24 sm:px-6" data-testid="capabilities-screens">
            <div className="mx-auto max-w-[1600px]">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <SectionHeader
                        eyebrow="MoovBytes Desktop"
                        title="A closer look at the MoovBytes experience."
                        body="A clean, powerful interface for managing your migrations."
                        align="left"
                    />
                    <a href="/documentation" className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-white/15 px-4 py-2 text-[12.5px] font-medium text-white/75 transition hover:border-white/30 hover:text-white">
                        See More Screenshots <ArrowRight size={13} />
                    </a>
                </div>
                <div className="mt-12 grid gap-6 lg:grid-cols-3">
                    {SCREENS.map((s) => (
                        <div key={s.title}>
                            <div className="text-[14px] font-bold text-white">{s.title}</div>
                            <p className="mt-1 text-[12.5px] leading-relaxed text-white/55">{s.body}</p>
                            <div className="mt-4">
                                <WindowsChrome title="MoovBytes">
                                    <div className="flex gap-3">
                                        <AppSidebar active={s.activeNav} />
                                        <div className="min-w-0 flex-1">{s.mockup}</div>
                                    </div>
                                </WindowsChrome>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function MockupDashboard() {
    const RECENT = [
        ["Migration to FS-01", "Completed", "#10b981", "2 hours ago"],
        ["Server migration started", "Info", "#3fb8ff", "4 hours ago"],
        ["Verification completed", "Completed", "#10b981", "5 hours ago"],
    ];
    return (
        <div className="space-y-2.5">
            <div>
                <div className="text-[11px] font-bold text-white">Dashboard</div>
                <div className="text-[8px] text-white/40">Monitor and manage your data migrations.</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
                {[["Active Migrations", "1"], ["Devices", "4"], ["Completed", "12"]].map(([k, v]) => (
                    <div key={k} className="rounded-md border border-white/5 bg-white/[0.03] p-2">
                        <div className="text-[7px] uppercase tracking-wide text-white/40">{k}</div>
                        <div className="mt-0.5 font-display text-base font-bold tabular-nums">{v}</div>
                    </div>
                ))}
            </div>
            <div className="rounded-md border border-white/5 bg-white/[0.02] p-2">
                <div className="mb-1 text-[7px] uppercase tracking-wide text-white/35">Recent Activity</div>
                {RECENT.map(([n, st, c, when]) => (
                    <div key={n} className="flex items-center justify-between py-0.5 text-[8px]">
                        <span className="flex items-center gap-1.5 text-white/70">
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />
                            {n}
                        </span>
                        <span className="text-white/40">{when}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function MockupNewMigration() {
    return (
        <div className="space-y-2.5">
            <div>
                <div className="text-[11px] font-bold text-white">New Migration</div>
                <div className="text-[8px] text-white/40">Select your source and destination to get started.</div>
            </div>
            <div className="flex items-center gap-1">
                {["Source", "Destination", "Options", "Review"].map((s, i) => (
                    <React.Fragment key={s}>
                        <div className={`flex h-4 w-4 items-center justify-center rounded-full text-[7px] font-bold ${i === 0 ? "bg-[#00C2FF] text-[#03122b]" : "border border-white/15 text-white/40"}`}>{i + 1}</div>
                        {i < 3 && <div className="h-px flex-1 bg-white/10" />}
                    </React.Fragment>
                ))}
            </div>
            <div className="rounded-md border border-white/5 bg-white/[0.02] p-2.5">
                <div className="text-[7px] uppercase tracking-wide text-white/40">Source</div>
                <div className="mt-1 text-[10px] text-white/80">Select files or folders from this device</div>
            </div>
            <div className="rounded-md border border-white/5 bg-white/[0.02] p-2.5">
                <div className="text-[7px] uppercase tracking-wide text-white/40">Destination</div>
                <div className="mt-1 text-[10px] text-white/80">Select a device on your network</div>
            </div>
        </div>
    );
}

function MockupMigrationDetails() {
    return (
        <div className="space-y-2.5">
            <div>
                <div className="text-[11px] font-bold text-white">Migration Details</div>
                <div className="text-[8px] text-white/40">PC migration to WIN-DEV-01</div>
            </div>
            <div className="flex items-end justify-between">
                <span className="font-display text-2xl font-bold tabular-nums">78%</span>
                <span className="text-[8px] text-white/50">1.46 TB / 1.87 TB</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5">
                <div className="h-full rounded-full bg-gradient-to-r from-[#5b5ef0] to-[#00C2FF]" style={{ width: "78%" }} />
            </div>
            <div className="flex items-center gap-1.5 border-b border-white/5 pb-2 text-[8px] text-white/40">
                <span className="border-b border-[#00C2FF] pb-1.5 text-[#3fb8ff]">Overview</span>
                <span>Files</span><span>Logs</span><span>Verification</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[8px]">
                <div className="flex justify-between"><span className="text-white/45">Source</span><span className="text-white/75">WIN-DEV-01</span></div>
                <div className="flex justify-between"><span className="text-white/45">Destination</span><span className="text-white/75">FS-01</span></div>
                <div className="flex justify-between"><span className="text-white/45">Files Transferred</span><span className="text-white/75">642 MB/s</span></div>
                <div className="flex justify-between"><span className="text-white/45">Speed</span><span className="text-white/75">642 MB/s</span></div>
            </div>
        </div>
    );
}

/* ─────────────────────────── Final CTA ─────────────────────────── */
function FinalCTA({ openContact }) {
    return (
        <section className="relative z-10 px-6 py-24 sm:px-6" data-testid="capabilities-cta">
            <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 rounded-3xl border border-white/10 bg-gradient-to-br from-[#00C2FF]/10 via-white/[0.03] to-[#5b5ef0]/15 p-10 text-center sm:p-16">
                <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-5xl">Ready to move your data?</h2>
                <p className="max-w-xl text-[15px] leading-relaxed text-white/70">Download MoovBytes for Windows and start your next migration.</p>
                <div className="mt-2 flex flex-col items-center gap-3 sm:flex-row">
                    <Link to={DOWNLOAD_HREF} className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#0066ff] px-8 py-3.5 text-sm font-semibold uppercase tracking-widest text-white shadow-lg shadow-[#004AAD]/30 transition hover:brightness-110">
                        <AppWindow size={16} />
                        Download for Windows
                    </Link>
                    <Link to="/documentation" className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/[0.04] px-8 py-3.5 text-sm font-semibold uppercase tracking-widest text-white/85 backdrop-blur transition hover:border-[#00C2FF]/50 hover:bg-white/[0.08] hover:text-white">
                        View Documentation
                    </Link>
                </div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/40">Windows 10/11 · Windows Server 2019–2025 · 64-bit</div>
                <button type="button" onClick={openContact} className="text-[13px] text-white/55 underline decoration-white/20 underline-offset-4 transition hover:text-white">
                    Planning a larger migration? Contact us.
                </button>
            </div>
        </section>
    );
}
